import { PageHeader } from '@/components/page-header'
import { H1, H2 } from '@/components/heading'
import { Highlight } from '@/components/highlight'
import { InlineLinks } from '@/components/inline-links'
import { Section } from '@/components/section'
import {
  Button,
  ButtonLink,
  Group,
  Icon,
  Lightbox,
  Link,
  Stack,
} from '@tempots/beatui'
import { attr, html, on } from '@tempots/dom'
import { HomeDropZone } from '@/components/home-drop-zone'
import { FeatureCard } from '@/components/feature-card'
import { PricingSection } from '@/components/pricing-section'
import { StepCard } from '@/components/step-card'
import { TestimonialCard } from '@/components/testimonial-card'
import { HR } from '@/components/hr'
import { RequireAnon } from '@/components/require-anon'
import { pricingPlans } from '@/data/pricing-plans'
import { Track } from '@/services/track'
import { SampleResumeLink } from '@/components/sample-resume-link'
import { Copyright } from '@/components/copyright'

export interface HomePageOptions {
  setResumeFile: (file: File) => void
}

export function HomePage({ setResumeFile }: HomePageOptions) {
  return RequireAnon({}, () =>
    Stack(
      attr.class('h-full overflow-y-auto'),
      PageHeader({
        center: InlineLinks({
          links: [
            { href: '#features', label: 'Features', element_id: 'features' },
            { href: '#pricing', label: 'Pricing', element_id: 'pricing' },
            {
              href: '#get-started',
              label: 'Get Started',
              element_id: 'get_started',
            },
          ],
        }),
        right: Group(
          attr.class('space-x-4 items-center pr-2'),
          ButtonLink(
            { href: '/signin', color: 'primary', variant: 'text' },
            on.click(() =>
              Track.click({
                section: 'top_bar',
                element_type: 'button',
                click_action: 'navigate',
                element_id: 'signin',
              })
            ),
            'Sign In'
          ),
          ButtonLink(
            { href: '/trial', color: 'primary', roundedness: 'xl', size: 'lg' },
            on.click(() =>
              Track.click({
                section: 'top_bar',
                element_type: 'button',
                click_action: 'navigate',
                element_id: 'start_for_free',
              })
            ),
            'Start for Free'
          )
        ),
      }),
      Section(
        { background: 'light' },
        H1(
          'Stop Losing Credit for',
          html.br(),
          Highlight('Your'),
          ' Candidates'
        ),
        html.div(
          attr.class('text-center text-lg text-gray-600'),
          'Add your header to candidate resumes and redact contact info with just 1 click.'
        ),
        html.div(
          attr.class('text-center text-lg text-gray-600'),
          'Maintain your brand consistency while showcasing top talent to clients.'
        ),
        html.div(
          attr.class('pt-8 pb-2 flex justify-center'),
          HomeDropZone({ setResumeFile })
        ),
        html.div(
          attr.class('text-center pb-8 text-xl'),
          SampleResumeLink({ setResumeFile }, 'Or try with a sample resume')
        ),
        Group(
          attr.class('space-x-4 justify-center'),
          ButtonLink(
            {
              href: '/signup',
              color: 'primary',
              roundedness: 'xl',
              size: 'lg',
            },
            on.click(() =>
              Track.click({
                section: 'home_drop_zone',
                element_type: 'button',
                click_action: 'navigate',
                element_id: 'start_for_free',
              })
            ),
            'Start your free 14-day trial'
          ),
          Lightbox(
            {
              dismissable: true,
              showCloseButton: true,
            },
            open => {
              return Button(
                { roundedness: 'xl', size: 'lg' },
                on.click(() => {
                  Track.click({
                    section: 'home_drop_zone',
                    element_type: 'button',
                    click_action: 'navigate',
                    element_id: 'watch_video',
                  })
                  open(
                    html.iframe(
                      attr.src('https://www.youtube.com/embed/hLMOour0xsw'),
                      attr.class(
                        'w-120 h-80 md:w-180 md:h-120 lg:w-240 lg:h-160 xl:w-300 xl:h-200'
                      )
                    )
                    // BaseVideoPlayer({
                    //   width: '80%',
                    //   height: '80%',
                    //   url: 'https://youtu.be/hLMOour0xsw',
                    // })
                  )
                }),
                'Watch Demo'
              )
            }
          )
        ),
        Group(
          attr.class('space-x-4 justify-center p-4 text-sm md:text-md'),
          Group(
            attr.class('items-center'),
            Icon({ icon: 'fe:check', color: 'teal' }),
            html.div('Every feature free for 14 days')
          ),
          Group(
            attr.class('items-center'),
            Icon({ icon: 'fe:check', color: 'teal' }),
            html.div('Cancel anytime')
          )
        ),
        html.div(
          attr.class('text-center text-lg text-gray-600'),
          'Set-up takes about 90 seconds. No credit card required.'
        ),
        H2(
          'Made for ',
          Highlight('Recruiters'),
          ', by ',
          Highlight('Recruiters')
        ),
        html.div(
          attr.class('text-center text-sm text-gray-600'),
          html.sup('*'),
          ' by using ResumeHeader you agree to our ',
          Link({ href: '/terms' }, 'terms of service')
        )
      ),
      Section(
        { background: 'white' },
        html.div(
          attr.class('flex flex-col sm:flex-row gap-4 w-full py-16'),
          TestimonialCard({
            quote:
              'Previously, my team was putting headers on resumes less than 30% of the time because they said it was just "too time consuming". By making the Resume Header process practically instant, now, 99% of our resumes go out with headers. I expect this tool to "revive" at least 1 lost deal a year worth tens of thousands of dollars. This might literally be the highest ROI tool in my entire recruiting rolodex.',
            name: 'Will Wegert, Partner  / Practice Manager - Direct Hire',
            avatar: '/testimonials/will-wegert.jpg',
          }),
          TestimonialCard({
            quote:
              'As a Senior Recruiter in the staffing and temp industry, I’m constantly sending resumes to clients. Before using this tool, adding headers and formatting resumes was a repetitive task that took several minutes per resume. Now it happens instantly. It has easily saved me several hours every week and keeps every resume looking polished and consistent.',
            name: 'Grant Miller, Senior Recruiter | Staffing & Temp',
            avatar: '/testimonials/grant-miller.jpg',
          })
        )
      ),
      PricingSection({
        plans: pricingPlans,
        displayMonthlyToggle: false,
      }),
      Section(
        { background: 'white' },
        attr.id('features'),
        H2('Why Recruiters Choose', html.br(), Highlight('ResumeHeaders')),
        html.div(
          attr.class('text-center text-lg text-gray-600'),
          'Streamline your recruitment process with powerful automation tools designed for modern recruiting teams.'
        ),
        html.div(
          attr.class(
            'flex flex-row items-start justify-around flex-wrap items-stretch gap-4 p-4'
          ),
          FeatureCard({
            iconUrl: '/fast-icon.svg',
            title: 'Lightning Fast',
            description:
              'Transform resumes in 2-3 seconds. No more manual formatting or design work.',
          }),
          FeatureCard({
            iconUrl: '/customize-icon.svg',
            title: 'Highly Customizable',
            description:
              'Match your firm’s branding perfectly, and choose how much flexibility to provide recruiters.',
          }),
          FeatureCard({
            iconUrl: '/team-icon.svg',
            title: 'Brand Consistency',
            description:
              'Maintain your agency’s professional image with custom headers and footers on every resume.',
          }),
          FeatureCard({
            iconUrl: '/redact-icon.svg',
            title: 'Instant Redaction',
            description:
              'Automatically redact sensitive information while preserving professional presentation.',
          }),
          FeatureCard({
            iconUrl: '/security-icon.svg',
            title: '100% Secure',
            description:
              'We NEVER save or share resumes. They belong to you. Always.',
          })
        )
      ),
      Section(
        { background: 'light' },
        attr.id('get-started'),
        H2('Get Started in Minutes'),
        html.div(
          attr.class('text-center text-lg text-gray-600'),
          'Follow these simple steps to start transforming your candidate resumes today.'
        ),
        html.div(
          attr.class('flex flex-row justify-between gap-4'),
          StepCard({
            color: 'blue',
            number: '1',
            title: 'Create Your Account',
            description:
              'Sign up for your free 14-day trial. No credit card required to get started.',
          }),
          StepCard({
            color: 'rose',
            number: '2',
            title: 'Customize',
            description:
              'Add your agency’s header and footer designs. Our system will take care of the rest.',
          }),
          StepCard({
            color: 'green',
            number: '3',
            title: 'Transform Resumes',
            description:
              'Upload candidate resumes and watch them transform instantly with your branding.',
          })
        ),
        html.div(
          attr.class('flex flex-row justify-center pt-4'),
          ButtonLink(
            {
              href: '/signup',
              color: 'primary',
              roundedness: 'xl',
              size: 'lg',
            },
            'Start your free 14-day trial'
          )
        )
      ),
      Section(
        { background: 'dark' },
        html.div(
          attr.class('grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8'),
          html.div(
            attr.class(
              'flex flex-col gap-4 col-span-1 sm:col-span-2 md:col-span-1'
            ),
            html.div(
              attr.class(
                'flex flex-row items-center gap-2 justify-center sm:justify-start'
              ),
              html.img(
                attr.class('h-12 bg-transparent'),
                attr.src('/logo-square-dark.svg')
              ),
              html.div(
                html.span(attr.class('text-teal-400'), 'Resume'),
                html.span(attr.class('text-amber-400'), 'Headers')
              )
            ),
            html.div(
              attr.class('text-gray-300 text-sm text-center sm:text-left'),
              'Transform candidate resumes with professional headers and footers in seconds.'
            )
          ),
          html.div(
            attr.class('flex flex-col gap-4 text-center sm:text-left'),
            html.div(attr.class('text-gray-400 font-semibold'), 'Product'),
            html.ul(
              html.li(
                html.a(
                  attr.href('#features'),
                  on.click(() =>
                    Track.click({
                      section: 'footer',
                      element_type: 'link',
                      click_action: 'navigate',
                      element_id: 'features',
                    })
                  ),
                  html.span(attr.class('text-white'), 'Features')
                )
              ),
              html.li(
                html.a(
                  attr.href('#pricing'),
                  on.click(() =>
                    Track.click({
                      section: 'footer',
                      element_type: 'link',
                      click_action: 'navigate',
                      element_id: 'pricing',
                    })
                  ),
                  html.span(attr.class('text-white'), 'Pricing')
                )
              )
            )
          ),
          html.div(
            attr.class('flex flex-col gap-4 text-center sm:text-left'),
            html.div(attr.class('text-gray-400 font-semibold'), 'Company'),
            html.ul(
              html.li(
                Link(
                  { href: '/about', color: 'white', variant: 'plain' },
                  on.click(() =>
                    Track.click({
                      section: 'footer',
                      element_type: 'link',
                      click_action: 'navigate',
                      element_id: 'about',
                    })
                  ),
                  'About'
                )
              ),
              html.li(
                Link(
                  { href: '/contact', color: 'white', variant: 'plain' },
                  on.click(() =>
                    Track.click({
                      section: 'footer',
                      element_type: 'link',
                      click_action: 'navigate',
                      element_id: 'contact',
                    })
                  ),
                  'Contact'
                )
              )
            )
          ),
          html.div(
            attr.class('flex flex-col gap-4 text-center sm:text-left'),
            html.div(attr.class('text-gray-400 font-semibold'), 'Support'),
            html.ul(
              html.li(
                Link(
                  {
                    href: '/faq',
                    color: 'white',
                    variant: 'plain',
                  },
                  on.click(() =>
                    Track.click({
                      section: 'footer',
                      element_type: 'link',
                      click_action: 'navigate',
                      element_id: 'faq',
                    })
                  ),
                  'FAQ & Documentation'
                )
              ),
              html.li(
                Link(
                  { href: '/privacy', color: 'white', variant: 'plain' },
                  on.click(() =>
                    Track.click({
                      section: 'footer',
                      element_type: 'link',
                      click_action: 'navigate',
                      element_id: 'privacy',
                    })
                  ),
                  'Privacy '
                )
              ),
              html.li(
                Link(
                  { href: '/terms', color: 'white', variant: 'plain' },
                  on.click(() =>
                    Track.click({
                      section: 'footer',
                      element_type: 'link',
                      click_action: 'navigate',
                      element_id: 'terms',
                    })
                  ),
                  'Terms of Service'
                )
              ),
              html.li(html.br()),
              html.li(
                Link(
                  {
                    href: 'mailto:support@resumeheaders.com',
                    color: 'white',
                    variant: 'plain',
                  },
                  on.click(() =>
                    Track.click({
                      section: 'footer',
                      element_type: 'link',
                      click_action: 'navigate',
                      element_id: 'contact_support',
                    })
                  ),
                  'support@resumeheaders.com'
                )
              )
            )
          )
        ),
        HR(),
        Copyright()
      )
    )
  )
}
